import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchProjectUpdates,
  createProjectUpdate,
  updateProjectUpdate,
  deleteProjectUpdate,
} from '../api/projectUpdatesApi'

export default function DeveloperUpdates({ projectId }) {
  const queryClient = useQueryClient()
  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({
    title: '',
    content: '',
  })

  const { data: updates = [], isLoading } = useQuery({
    queryKey: ['project-updates', projectId],
    queryFn: () => fetchProjectUpdates(projectId),
    enabled: !!projectId,
  })

  const createMutation = useMutation({
    mutationFn: createProjectUpdate,
    onSuccess: () => {
      queryClient.invalidateQueries(['project-updates', projectId])
      setIsAdding(false)
      resetForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }) => updateProjectUpdate(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['project-updates', projectId])
      setEditingId(null)
      resetForm()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteProjectUpdate,
    onSuccess: () => {
      queryClient.invalidateQueries(['project-updates', projectId])
    },
  })

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
    })
  }

  const handleAdd = () => {
    setIsAdding(true)
    setEditingId(null)
    resetForm()
  }

  const handleEdit = (update) => {
    setEditingId(update.id)
    setIsAdding(false)
    setFormData({
      title: update.title,
      content: update.content,
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!formData.title.trim() || !formData.content.trim()) return

    const updateData = {
      project_id: projectId,
      title: formData.title.trim(),
      content: formData.content.trim(),
    }

    if (editingId) {
      updateMutation.mutate({ id: editingId, updates: updateData })
    } else {
      createMutation.mutate(updateData)
    }
  }

  const handleCancel = () => {
    setIsAdding(false)
    setEditingId(null)
    resetForm()
  }

  const handleDelete = (id) => {
    if (confirm('Are you sure you want to delete this update?')) {
      deleteMutation.mutate(id)
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString('en-NZ', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-6 bg-secondary-200 rounded w-1/3" />
        <div className="h-32 bg-secondary-100 rounded" />
        <div className="h-32 bg-secondary-100 rounded" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-secondary-900">Project Updates</h3>
          <p className="text-sm text-secondary-600 mt-1">
            Share progress updates and news with buyers
          </p>
        </div>
        {!isAdding && !editingId && (
          <button onClick={handleAdd} className="btn-primary">
            Add Update
          </button>
        )}
      </div>

      {(isAdding || editingId) && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 bg-secondary-50 rounded-lg">
          <div className="space-y-4">
            <div>
              <label className="label">Update Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="input"
                placeholder="e.g., Construction Progress - Week 12"
                required
              />
            </div>

            <div>
              <label className="label">Update Content *</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="input"
                rows="6"
                placeholder="Share details about this update..."
                required
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="btn-primary"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? 'Saving...'
                  : editingId
                  ? 'Update'
                  : 'Publish Update'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="btn-outline"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      {updates.length === 0 ? (
        <div className="text-center py-8 text-secondary-600">
          <p>No updates yet. Share your first update to keep buyers informed.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {updates.map((update) => (
            <div
              key={update.id}
              className="p-6 rounded-lg border border-secondary-200 bg-white"
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-secondary-900 text-lg mb-1">
                    {update.title}
                  </h4>
                  <div className="text-sm text-secondary-500">
                    {formatDate(update.created_at)} at {formatTime(update.created_at)}
                    {update.updated_at !== update.created_at && (
                      <span className="ml-2">(edited)</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleEdit(update)}
                    className="text-primary-600 hover:text-primary-700 p-1"
                    title="Edit update"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(update.id)}
                    className="text-red-600 hover:text-red-700 p-1"
                    disabled={deleteMutation.isPending}
                    title="Delete update"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              <p className="text-secondary-700 whitespace-pre-wrap">{update.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
